import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, AlertTriangle, RotateCcw, FileSpreadsheet, Archive as ArchiveIcon } from "lucide-react";

const GROUPS = [
  ["APPROVE", "Approve", "bg-emerald-500"],
  ["NEEDS_CHANGES", "Needs Changes", "bg-amber-500"],
  ["MERGE", "Merge", "bg-violet-500"],
  ["REMOVE", "Remove", "bg-slate-500"],
  ["NOT_REVIEWED", "Not Reviewed", "bg-slate-300 text-slate-700"],
  ["CONFLICT", "Conflict / Error", "bg-red-500"],
];

function RowCard({ r }) {
  return (
    <Card className="p-3 text-sm" data-testid={`import-row-${r.article_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="font-mono text-xs text-slate-500">{r.article_id}</span>
        <Badge className="bg-amber-500">{r.product_category}</Badge>
        <Badge variant="outline">{r.current_status}</Badge>
        <Badge variant="outline">→ {r.review_status}</Badge>
        {r.overlap && <Badge className="bg-red-500">Overlap with Approved</Badge>}
      </div>
      <div className="font-semibold text-slate-900">{r.problem}</div>
      {r.reviewed_by && <div className="text-xs text-slate-500">Reviewed by: {r.reviewed_by}</div>}
      {r.changes_required && <div className="mt-1"><span className="text-xs font-bold text-slate-400 uppercase">Changes required</span><div>{r.changes_required}</div></div>}
      {r.final_wording && <div className="mt-1"><span className="text-xs font-bold text-slate-400 uppercase">Proposed final wording</span><div className="whitespace-pre-line bg-blue-50 rounded p-2">{r.final_wording}</div></div>}
      {r.review_notes && <div className="mt-1 text-slate-600"><span className="text-xs font-bold text-slate-400 uppercase">Notes</span> {r.review_notes}</div>}
      {r.overlap && <div className="mt-1 text-xs text-red-700">{r.overlap}</div>}
      {r.warnings?.length > 0 && (
        <div className="mt-1 flex items-start gap-1 text-xs text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{r.warnings.join(" · ")}</span>
        </div>
      )}
    </Card>
  );
}

export function ReviewImportButton({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publish, setPublish] = useState(false);
  const fileRef = useRef();

  const reset = () => { setPreview(null); setPublish(false); if (fileRef.current) fileRef.current.value = ""; };

  const runPreview = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/review-import/preview", fd);
      setPreview(r.data);
      toast.success("Preview ready — nothing has been changed yet");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not read the spreadsheet");
    } finally { setBusy(false); }
  };

  const cancelImport = async () => {
    if (preview?.batch_id) { try { await api.post(`/admin/review-import/${preview.batch_id}/cancel`); } catch {} }
    reset(); setOpen(false);
  };

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/review-import/${preview.batch_id}/apply`, { publish_approved: publish });
      const a = r.data.applied;
      toast.success(`Applied: ${a.approved} approved, ${a.needs_changes} needs-changes, ${a.merge} merge, ${a.remove} removed${a.published ? `, ${a.published} published` : ""}`);
      reset(); setOpen(false); onApplied && onApplied();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        className="gap-2 border-mlily-blue/40 text-mlily-blue hover:bg-mlily-blue hover:text-white"
        data-testid="import-reviewed-drafts-btn">
        <Upload className="w-4 h-4" /> Import Reviewed Drafts
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) cancelImport(); else setOpen(true); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
          <DialogHeader><DialogTitle>Import Reviewed Drafts</DialogTitle></DialogHeader>

          {!preview ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload the completed <b>MLILY_Draft_Troubleshooting_Review.xlsx</b>. Uploading only builds a preview — nothing in the system changes until you confirm.</p>
              <input ref={fileRef} type="file" accept=".xlsx" data-testid="import-file-input"
                onChange={(e) => runPreview(e.target.files?.[0])}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-mlily-blue file:text-white hover:file:bg-mlily-bluehover" />
              {busy && <p className="text-sm text-slate-400">Reading spreadsheet…</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center text-sm" data-testid="import-counts">
                <span className="font-semibold">{preview.total_rows} rows uploaded</span>
                {GROUPS.map(([k, label, cls]) => (
                  <Badge key={k} className={cls}>{preview.counts[k]} {label}</Badge>
                ))}
              </div>
              {GROUPS.map(([k, label]) => {
                const rows = preview.rows.filter((r) => r.group === k);
                if (rows.length === 0) return null;
                return (
                  <div key={k}>
                    <div className="font-heading font-bold text-slate-900 mt-2 mb-1">{label} ({rows.length})</div>
                    <div className="grid gap-2">{rows.map((r) => <RowCard key={r.article_id} r={r} />)}</div>
                  </div>
                );
              })}
              <label className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded p-3" data-testid="publish-approved-toggle">
                <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} />
                <span>Also <b>publish</b> Approved articles now (make visible to staff). Default OFF — approved items stay Draft, marked "Approved for Publishing", and you publish them separately. Overlapping items are never auto-published.</span>
              </label>
            </div>
          )}

          <DialogFooter>
            {preview && (
              <>
                <Button variant="outline" onClick={cancelImport} disabled={busy} data-testid="cancel-import-btn">Cancel Import</Button>
                <Button className="bg-mlily-blue hover:bg-mlily-bluehover" onClick={apply} disabled={busy} data-testid="apply-import-btn">Apply Reviewed Changes</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function KbImportButton({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const reset = () => { setPreview(null); if (fileRef.current) fileRef.current.value = ""; };
  const runPreview = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/kb-import/preview", fd);
      setPreview(r.data);
      toast.success("Preview ready — nothing imported yet");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not read workbook"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (preview?.batch_id) { try { await api.post(`/admin/kb-import/${preview.batch_id}/cancel`); } catch {} }
    reset(); setOpen(false);
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/kb-import/${preview.batch_id}/apply`);
      const a = r.data;
      toast.success(`Imported ${a.imported}, updated ${a.updated}, failed ${a.failed}`);
      reset(); setOpen(false); onApplied && onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };
  const c = preview?.counts || {};
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        className="gap-2 border-emerald-500/40 text-emerald-700 hover:bg-emerald-600 hover:text-white"
        data-testid="kb-import-btn"><FileSpreadsheet className="w-4 h-4" /> Import Knowledge Base (Excel)</Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); else setOpen(true); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="kb-import-dialog">
          <DialogHeader><DialogTitle>Import Troubleshooting Knowledge Base</DialogTitle></DialogHeader>
          {!preview ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload the completed MLILY troubleshooting workbook (.xlsx). Uploading only builds a preview — nothing is imported until you confirm. Re-importing updates matching articles instead of duplicating them.</p>
              <input ref={fileRef} type="file" accept=".xlsx" data-testid="kb-file-input"
                onChange={(e) => runPreview(e.target.files?.[0])}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white" />
              {busy && <p className="text-sm text-slate-400">Reading…</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-sm" data-testid="kb-counts">
                <span className="font-semibold">{preview.total_rows} rows detected</span>
                <Badge className="bg-emerald-500">{c.valid} valid</Badge>
                <Badge className="bg-blue-500">{c.new} new</Badge>
                <Badge className="bg-amber-500">{c.update} update</Badge>
                <Badge className="bg-red-500">{c.invalid} invalid</Badge>
                <Badge className="bg-slate-500">{c.duplicate_in_file} dup-in-file</Badge>
              </div>
              <div className="grid gap-1 max-h-[50vh] overflow-y-auto">
                {preview.rows.map((r) => (
                  <div key={r.excel_row} className="text-sm border rounded p-2" data-testid={`kb-row-${r.excel_row}`}>
                    <span className="font-mono text-xs text-slate-500">row {r.excel_row}</span>{" "}
                    <Badge className="bg-amber-500">{r.product_category || "—"}</Badge>{" "}
                    <Badge variant="outline">{r.action}</Badge>{" "}
                    {r.duplicate_in_file && <Badge className="bg-slate-500">dup</Badge>}{" "}
                    <span className="text-slate-900">{r.problem || "(no problem)"}</span>
                    <span className="text-xs text-slate-400"> · {r.steps_count} steps</span>
                    {r.errors?.length > 0 && <div className="text-xs text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.errors.join(", ")}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            {preview && (<>
              <Button variant="outline" onClick={cancel} disabled={busy} data-testid="kb-cancel-btn">Cancel Import</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={apply} disabled={busy || c.valid === 0} data-testid="kb-apply-btn">Import {c.valid} Article(s)</Button>
            </>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReviewImportsTab() {
  const [batches, setBatches] = useState([]);
  const [detail, setDetail] = useState(null);
  const [archived, setArchived] = useState([]);
  const [pub, setPub] = useState({ ready: [], blocked: [], ready_count: 0, blocked_count: 0 });
  const [confirmPub, setConfirmPub] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);

  const load = () => {
    api.get("/admin/review-imports").then((r) => setBatches(r.data)).catch(() => {});
    api.get("/admin/archived-articles").then((r) => setArchived(r.data)).catch(() => {});
    api.get("/admin/approved-for-publishing").then((r) => setPub(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const doPublish = async () => {
    setPubBusy(true);
    try {
      const r = await api.post("/admin/publish-approved-drafts");
      toast.success(`Published ${r.data.published} article(s)${r.data.skipped_overlap ? `, skipped ${r.data.skipped_overlap} overlap(s)` : ""}`);
      setConfirmPub(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Publish failed");
    } finally { setPubBusy(false); }
  };

  const openDetail = async (id) => {
    const r = await api.get(`/admin/review-imports/${id}`);
    setDetail(r.data);
  };
  const revert = async (id) => {
    try { await api.post(`/admin/review-import/${id}/revert`); toast.success("Batch reverted — articles restored"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Revert failed"); }
  };
  const restore = async (aid) => {
    try { await api.post(`/admin/articles/${aid}/restore-archived`); toast.success("Article restored to drafts"); load(); }
    catch (e) { toast.error("Restore failed"); }
  };

  return (
    <div className="space-y-6" data-testid="review-imports-tab">
      <Card className="p-4 border-emerald-300 bg-emerald-50/50" data-testid="bulk-publish-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading font-bold text-slate-900">Ready to publish</div>
            <div className="text-sm text-slate-600">
              <b>{pub.ready_count}</b> draft(s) marked "Approved for Publishing" are ready to go live to staff.
              {pub.blocked_count > 0 && <> <b>{pub.blocked_count}</b> approved draft(s) are held back pending a Merge/Keep decision (overlap with existing Approved articles).</>}
            </div>
          </div>
          <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" disabled={pub.ready_count === 0}
            onClick={() => setConfirmPub(true)} data-testid="bulk-publish-btn">
            <Upload className="w-4 h-4" /> Publish {pub.ready_count} Approved Draft(s)
          </Button>
        </div>
      </Card>
      <Card>
        <div className="p-4 font-heading font-bold text-slate-900 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-mlily-blue" /> Review Imports</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Uploaded by</TableHead><TableHead>File</TableHead>
            <TableHead>Rows</TableHead><TableHead>Appr</TableHead><TableHead>Chg</TableHead><TableHead>Merge</TableHead>
            <TableHead>Rem</TableHead><TableHead>NotRev</TableHead><TableHead>Conflict</TableHead>
            <TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {batches.map((b) => (
              <TableRow key={b.id} data-testid={`import-batch-${b.id}`}>
                <TableCell className="text-xs">{(b.uploaded_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="text-sm">{b.uploaded_by}</TableCell>
                <TableCell className="text-xs">{b.filename}</TableCell>
                <TableCell>{b.total_rows}</TableCell>
                <TableCell>{b.counts?.APPROVE ?? 0}</TableCell>
                <TableCell>{b.counts?.NEEDS_CHANGES ?? 0}</TableCell>
                <TableCell>{b.counts?.MERGE ?? 0}</TableCell>
                <TableCell>{b.counts?.REMOVE ?? 0}</TableCell>
                <TableCell>{b.counts?.NOT_REVIEWED ?? 0}</TableCell>
                <TableCell>{b.counts?.CONFLICT ?? 0}</TableCell>
                <TableCell><Badge className={b.status === "applied" ? "bg-emerald-500" : b.status === "reverted" ? "bg-red-500" : b.status === "cancelled" ? "bg-slate-400" : "bg-amber-500"}>{b.status}</Badge></TableCell>
                <TableCell className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openDetail(b.id)} data-testid={`open-batch-${b.id}`}>Open</Button>
                  {b.status === "applied" && <Button size="sm" variant="outline" onClick={() => revert(b.id)} className="gap-1 text-red-600"><RotateCcw className="w-3.5 h-3.5" />Revert</Button>}
                </TableCell>
              </TableRow>
            ))}
            {batches.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-6">No imports yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Card>
        <div className="p-4 font-heading font-bold text-slate-900 flex items-center gap-2"><ArchiveIcon className="w-5 h-5 text-slate-500" /> Archived / Rejected Drafts ({archived.length})</div>
        <Table>
          <TableHeader><TableRow><TableHead>Article ID</TableHead><TableHead>Category</TableHead><TableHead>Issue</TableHead><TableHead>Reviewed by</TableHead><TableHead>Notes</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {archived.map((a) => (
              <TableRow key={a.id} data-testid={`archived-${a.id}`}>
                <TableCell className="font-mono text-xs">{a.id}</TableCell>
                <TableCell>{a.product_category}</TableCell>
                <TableCell>{a.problem}</TableCell>
                <TableCell className="text-sm">{a.review_meta?.reviewed_by}</TableCell>
                <TableCell className="text-xs text-slate-500">{a.review_meta?.review_notes}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => restore(a.id)}>Restore</Button></TableCell>
              </TableRow>
            ))}
            {archived.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No archived drafts.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={confirmPub} onOpenChange={(v) => !v && setConfirmPub(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-publish-dialog">
          <DialogHeader><DialogTitle>Publish Approved Drafts</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>This will set <b>{pub.ready_count}</b> draft(s) to <b>Approved</b> — they become visible to all staff immediately.</p>
            <div className="grid gap-1">
              {pub.ready.map((r) => (
                <div key={r.id} className="flex items-center gap-2 border rounded p-2" data-testid={`pub-ready-${r.id}`}>
                  <Badge className="bg-amber-500">{r.product_category}</Badge>
                  <span className="text-slate-900">{r.problem}</span>
                </div>
              ))}
            </div>
            {pub.blocked_count > 0 && (
              <div className="mt-2">
                <div className="text-red-700 font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Held back — overlap with existing Approved article ({pub.blocked_count})</div>
                <div className="grid gap-1 mt-1">
                  {pub.blocked.map((r) => (
                    <div key={r.id} className="border border-red-200 rounded p-2 text-xs">
                      <b>{r.problem}</b> — {r.overlap}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">These are not published. Resolve MERGE / KEEP SEPARATE first.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPub(false)} disabled={pubBusy}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doPublish} disabled={pubBusy || pub.ready_count === 0} data-testid="confirm-bulk-publish-btn">
              {pubBusy ? "Publishing…" : `Publish ${pub.ready_count} now`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import batch — {detail?.filename}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-500">{detail.uploaded_by} · {(detail.uploaded_at || "").slice(0, 16).replace("T", " ")} · status <b>{detail.status}</b></div>
              <div className="flex flex-wrap gap-2">{GROUPS.map(([k, label, cls]) => <Badge key={k} className={cls}>{detail.counts?.[k] ?? 0} {label}</Badge>)}</div>
              <div className="font-heading font-bold mt-2">Changes applied ({detail.audit?.length || 0})</div>
              {(detail.audit || []).map((au, i) => (
                <div key={i} className="border rounded p-2">
                  <span className="font-mono text-xs">{au.article_id}</span> — <b>{au.action}</b>
                  <div className="text-xs text-slate-500">prev status: {au.prev_status} → {au.new_review_status}{au.published ? " (published)" : ""} · reviewed by {au.reviewed_by || "—"}</div>
                </div>
              ))}
              {(!detail.audit || detail.audit.length === 0) && <p className="text-slate-400">No changes recorded (preview/cancelled).</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
